/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { func, bool, string, array, object } from 'prop-types';
import {
  ModalStyled,
  ModalActionsStyled,
  ButtonStyled,
  RadioSetStyled,
  RadioStyled
} from '@nike/nike-design-system-components';

import { TranslatedCopy } from '../../intl/TranslatedCopy';
import OrdersTable from './OrdersTable';
import { DownloadBanner } from '../../components/DownloadBanner';

import {
  requestOrders,
  requestRemainingOrderIds,
  generateAvailabilityReport,
  CLEAR_ORDERS,
  REQUEST_IDS_IN_PROGRESS,
  CANCEL_REQUEST_IDS_IN_PROGRESS
} from './actions';
import { requestsActions } from '../../requests/actions';
import { orderDetailsReportSelectors } from './reducer';

import { useDispatchWithProgress } from '../../hooks/useDispatchWithProgress';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

import { ORDERTYPE_CODE_MAP } from '../constants';
import { ORDER_TYPE_FORECAST, DEFAULT_DATE_RANGE } from '../../shop/constants';

const DownloadOrderDetailsReport = ({ isOpen, closeModal, accountNumber, departments, sort }) => {
  const dispatch = useDispatch();
  const getOrders = useDispatchWithProgress(requestOrders);
  const getAvailabilityReport = useDispatchWithProgress(generateAvailabilityReport);

  const [orderTypeSelected, setOrderTypeSelected] = useState(ORDER_TYPE_FORECAST);

  const orderCount = useSelector(orderDetailsReportSelectors.orderCount);
  const totalRecords = useSelector(orderDetailsReportSelectors.totalRecords);
  const pageNumber = useSelector(orderDetailsReportSelectors.pageNumber);

  const loadedOrderIds = useSelector(orderDetailsReportSelectors.orderIdsSelectedMap);
  const orderIdsSelected = Object.keys(loadedOrderIds).filter(id => loadedOrderIds[id]);
  const noOrdersSelected = Object.values(loadedOrderIds).every(value => !value);

  const [showDownloadBanner, setShowDownloadBanner] = useState(false);

  const isReportDownloading = useSelector(orderDetailsReportSelectors.downloadInProgress);
  const hasDownloadError = useSelector(orderDetailsReportSelectors.downloadError);

  const selectAllClicked = useSelector(orderDetailsReportSelectors.selectAllClicked);

  const getOrdersArgs = {
    payload: {
      accountNumber,
      dateRange: { period: DEFAULT_DATE_RANGE.toUpperCase() },
      departments,
      orderTypes: [ORDERTYPE_CODE_MAP[orderTypeSelected]],
      sort,
      page: { number: pageNumber, size: 10 }
    }
  };

  const memoizedGetOrders = useCallback(
    ([entry]) => {
      if (entry.isIntersecting) {
        getOrders(getOrdersArgs);
      }
    },
    [orderCount, orderTypeSelected]
  );

  const [rootRef, targetRef] = useIntersectionObserver({ callback: memoizedGetOrders });

  useEffect(() => {
    if (isOpen && orderCount > 0 && orderTypeSelected) {
      dispatch({ type: CLEAR_ORDERS });
    }
  }, [orderTypeSelected, isOpen]);

  useEffect(() => {
    if (isOpen && orderCount === 0 && orderTypeSelected) {
      getOrders(getOrdersArgs);
    }
  }, [orderCount, orderTypeSelected, isOpen]);

  useEffect(() => {
    if (hasDownloadError) {
      dispatch({ type: CANCEL_REQUEST_IDS_IN_PROGRESS });
    }
  }, [hasDownloadError]);

  const handleCloseModal = () => {
    closeModal();
    dispatch({ type: CLEAR_ORDERS });
  };

  const handleDownloadReport = async () => {
    dispatch({ type: REQUEST_IDS_IN_PROGRESS });
    closeModal();
    setShowDownloadBanner(true);
    const remainingIds =
      selectAllClicked && orderCount < totalRecords
        ? await requestRemainingOrderIds({ ...getOrdersArgs, startIndex: orderCount })
        : [];
    getAvailabilityReport({ orderIds: [...orderIdsSelected, ...remainingIds], orderType: orderTypeSelected });
  };

  const handleCloseBanner = () => {
    setShowDownloadBanner(false);
    if (hasDownloadError) {
      dispatch({ type: requestsActions.clearOrderDetailsReportError.name });
    }
  };

  const Actions = () => (
    <ModalActionsStyled>
      <ButtonStyled disabled={noOrdersSelected} onClick={handleDownloadReport}>
        <TranslatedCopy id='global.download' />
      </ButtonStyled>
      <ButtonStyled appearance='secondary' onClick={handleCloseModal}>
        <TranslatedCopy id='global.cancel' />
      </ButtonStyled>
    </ModalActionsStyled>
  );

  return (
    <>
      <ModalStyled
        isOpen={isOpen}
        closeModal={handleCloseModal}
        title=''
        modalRootCompProps={{ style: { maxWidth: '1200px' } }}
        slots={{
          HeaderTitleSlot: <TranslatedCopy id='orderstatus.download.orderDetailsReport' />,
          FooterSlot: <Actions />
        }}
      >
        <RadioSetStyled
          legend=''
          name='orderType'
          onChange={e => setOrderTypeSelected(e.target.value)}
          selectedValue={orderTypeSelected}
        >
          <RadioStyled
            id='forecast-radioButton'
            label={new TranslatedCopy({ id: 'global.order.type.forecast' }).toString()}
            value={ORDER_TYPE_FORECAST}
          />
          {/* FUTURE STATE: add At-Once and Futures <RadioStyled/> options */}
        </RadioSetStyled>
        <OrdersTable rootRef={rootRef} targetRef={targetRef} />
      </ModalStyled>
      {showDownloadBanner && (
        <DownloadBanner
          title={<TranslatedCopy id='orderstatus.orderDetailsReport' />}
          isLoading={isReportDownloading}
          hasError={hasDownloadError}
          closeBanner={handleCloseBanner}
        />
      )}
    </>
  );
};

DownloadOrderDetailsReport.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
  accountNumber: string.isRequired,
  departments: array.isRequired,
  sort: object.isRequired
};

export default DownloadOrderDetailsReport;
